import React, { useState, useEffect } from 'react';
import { TextField, MenuItem, Typography, Box } from '@mui/material';
import { shades } from '../../../theme';

const titles = ['Dr.', 'Prof.', 'Specialist', 'Surgeon'];
const specializations = ['Cardiology', 'Neurology', 'Pediatrics', 'Orthopedics', 'Dermatology'];

const PersonalInfo = ({ onValidityChange, onDataChange, initialData = {} }) => {
  // Use a single formData state object instead of separate states
  const [formData, setFormData] = useState({
    title: '',
    bio: '',
    specialization: '',
    customSpecialization: ''
  });
  const [errors, setErrors] = useState({});

  // Load saved data from localStorage when component mounts
  useEffect(() => {
    const storedData = localStorage.getItem('personalInfoData');
    if (storedData) {
      const data = JSON.parse(storedData);
      setFormData({
        title: data.title || initialData.title || '',
        bio: data.bio || initialData.bio || '',
        specialization: data.specialization || initialData.specialization || '',
        customSpecialization: data.customSpecialization || initialData.customSpecialization || ''
      });
    }
  }, [initialData]);

  // Handle input changes in a unified way
  const handleChange = (field, value) => {
    const updatedData = { ...formData, [field]: value };
    setFormData(updatedData);

    // Clear error when field is filled
    if (value && errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }

    // Validate the form when a change occurs
    validateForm(updatedData);

    // Update parent component with new data
    if (onDataChange) {
      onDataChange(updatedData);
    }

    // Save to localStorage
    localStorage.setItem('personalInfoData', JSON.stringify(updatedData));
  };

  const validateForm = (data = formData) => {
    const newErrors = {};
    if (!data.title) newErrors.title = 'Title is required.';
    if (data.bio.trim().length < 50) newErrors.bio = 'Bio must be at least 50 characters.';
    if (!data.specialization && !data.customSpecialization) {
      newErrors.specialization = 'At least one specialization is required.';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Style configuration for text fields
  const textFieldStyle = {
    backgroundColor: '#ffffff',
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#ffffff',
      '& fieldset': {
        borderColor: '#9e9e9e'
      },
      '&:hover fieldset': {
        borderColor: '#1976d2'
      },
      '&.Mui-focused': {
        backgroundColor: '#ffffff !important',
        '& fieldset': {
          borderColor: '#1976d2'
        }
      }
    },
    '& .MuiInputLabel-root': {
      color: '#9e9e9e'
    },
    '& .MuiOutlinedInput-input': {
      color: shades.neutral[700]
    },
    '& .MuiSelect-select': {
      color: shades.neutral[700]
    },
    '& input:-webkit-autofill': {
      WebkitBoxShadow: '0 0 0 1000px #ffffff inset',
      transition: 'background-color 5000s ease-in-out 0s'
    }
  };

  // Automatically check form validity whenever any field changes
  useEffect(() => {
    const isValid =
      formData.title &&
      formData.bio.trim().length >= 50 &&
      (formData.specialization || formData.customSpecialization);

    if (onValidityChange) {
      onValidityChange(isValid);
    }

    const timeout = setTimeout(() => {
      if (isValid && onDataChange) {
        onDataChange(formData);
      }
    }, 300); // 300ms debounce to avoid rapid updates

    return () => clearTimeout(timeout);
  }, [formData, onValidityChange, onDataChange]);

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 3,
        color: 'black',
        height: 'auto'
      }}
    >
      {/* Title Field */}
      <TextField
        select
        label='Title'
        value={formData.title}
        onChange={(e) => handleChange('title', e.target.value)}
        onBlur={() => {
          if (!formData.title) {
            setErrors((prev) => ({ ...prev, title: 'Title is required.' }));
          }
        }}
        fullWidth
        error={!!errors.title}
        helperText={errors.title}
        sx={textFieldStyle}
      >
        {titles.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>

      {/* Bio Field */}
      <TextField
        label='Professional Bio'
        multiline
        rows={4}
        fullWidth
        value={formData.bio}
        onChange={(e) => handleChange('bio', e.target.value)}
        error={!!errors.bio || (formData.bio.trim().length > 0 && formData.bio.trim().length < 50)}
        helperText={
          formData.bio.trim().length < 50 ? 'Bio must be at least 50 characters.' : ''
        }
        sx={textFieldStyle}
      />

      {/* Specialization Field */}
      <TextField
        select
        label='Specialization'
        value={formData.specialization}
        onChange={(e) => handleChange('specialization', e.target.value)}
        fullWidth
        error={!!errors.specialization}
        helperText={errors.specialization}
        sx={textFieldStyle}
      >
        {specializations.map((option) => (
          <MenuItem key={option} value={option}>
            {option}
          </MenuItem>
        ))}
      </TextField>

      {/* Custom Specialization Input */}
      <TextField
        label='Other Specialization (if not listed)'
        fullWidth
        value={formData.customSpecialization}
        onChange={(e) => handleChange('customSpecialization', e.target.value)}
        sx={textFieldStyle}
      />
    </Box>
  );
};

export default PersonalInfo;
